// testPatients.js — fixture-patient registry.
//
// Each entry encodes the encounter type + subspecialty for a seeded test
// patient. The top-bar uses this to render "Foothill Pulmonary test follow
// up sleep patient" instead of the normal "clinic · date" pair — the word
// "test" is the at-a-glance signal that you're not in a real chart.
//
// This also serves as the canonical fixture inventory for the subspecialty
// pitch-fixture library (Task #171): adding a new test patient means adding
// an entry here.
//
// Mirrors the `label` field in:
//   - sql/seed/foothill_thursday_fixtures_seed.py (FOOTHILL01..04)
//   - sql/seed/kenji_sleep_reports_seed.py (SPRINT0013)
//
// Keep this list in sync when adding new fixtures.

// Each fixture also carries fake `pcps` + `consultants` so the Share dropdown
// and the SendToPcpModal autofill have realistic dropdown data without
// needing the real physician-roster backend (Task #190 Phase 2) wired up
// yet. Numbers are obviously fake (555-prefix).
const FIXTURES = {
  // Foothill Thursday demo fixtures (Ponnezhan 2026-07-02)
  FOOTHILL01: {
    encounter: 'new', subspecialty: 'sleep',
    pcps: [
      { name: 'Dr. Sarah Kim',     specialty: 'Internal Medicine', fax: '(626) 555-0142', email: 'skim@arcadiamed.example' },
    ],
    consultants: [
      { name: 'Dr. Jim Patel',     specialty: 'Pulmonology',       fax: '(626) 555-0188', email: 'jpatel@foothillpulm.example' },
    ],
  },
  FOOTHILL02: {
    encounter: 'new', subspecialty: 'pulmonary',
    pcps: [
      { name: 'Dr. Robert Tanaka', specialty: 'Family Medicine',   fax: '(626) 555-0156', email: 'rtanaka@familymed.example' },
    ],
    consultants: [
      { name: 'Dr. Anna Vasquez',  specialty: 'Allergy/Immunology',fax: '(626) 555-0177', email: 'avasquez@allergyca.example' },
    ],
  },
  FOOTHILL03: {
    encounter: 'follow up', subspecialty: 'pulmonary',
    pcps: [
      { name: 'Dr. Maria Garcia',  specialty: 'Internal Medicine', fax: '(626) 555-0167', email: 'mgarcia@arcadiamed.example' },
    ],
    consultants: [
      { name: 'Dr. James Chen',    specialty: 'Cardiology',        fax: '(626) 555-0199', email: 'jchen@cardiogroup.example' },
      { name: 'Dr. Linda Park',    specialty: 'Rheumatology',      fax: '(626) 555-0204', email: 'lpark@rheumca.example'     },
    ],
  },
  FOOTHILL04: {
    encounter: 'follow up', subspecialty: 'sleep',
    pcps: [
      { name: 'Dr. Maria Garcia',  specialty: 'Internal Medicine', fax: '(626) 555-0167', email: 'mgarcia@arcadiamed.example' },
    ],
    consultants: [
      { name: 'Dr. James Chen',    specialty: 'Cardiology',        fax: '(626) 555-0199', email: 'jchen@cardiogroup.example' },
      { name: 'Dr. Priya Shah',    specialty: 'ENT',               fax: '(626) 555-0215', email: 'pshah@entca.example'       },
    ],
  },
  // Kenji — Leejoe's primary dogfood patient (always-today via FOREVER_TODAY)
  SPRINT0013: {
    encounter: 'follow up', subspecialty: 'sleep',
    pcps: [
      { name: 'Dr. David Yamamoto',specialty: 'Internal Medicine', fax: '(626) 555-0123', email: 'dyamamoto@internalmedca.example' },
    ],
    consultants: [
      { name: 'Dr. Rachel Wong',   specialty: 'Pulmonology',       fax: '(626) 555-0134', email: 'rwong@foothillpulm.example' },
    ],
  },
};

export function isFixturePatient(ehrId) {
  if (!ehrId) return false;
  return Object.prototype.hasOwnProperty.call(FIXTURES, ehrId);
}

// Task #229 (Leejoe 2026-06-29): UUID-keyed fixture lookup. Use this when
// you need to gate UI on "is this a fixture patient" but DON'T have the
// EHR_ID handy — typical case is a component that only has the chart UUID
// from the URL and can't easily fetch the notes-c row (DoctorPortalBar's
// row fetch hit RLS issues querying public.notes-c from MEDSTUD-scoped
// users). Keep this list in sync with FOREVER_TODAY_ROW_IDS in
// useTodaysSchedule.js.
const FIXTURE_UUIDS = new Set([
  'f7e19fd4-4d4f-4473-9a99-b7ede1a63212', // SPRINT0013 — Yamashita, Kenji
  '75367f21-56d3-4a22-a87e-60278fe890e5', // FOOTHILL01 — Cheney, Richard
  '6f9c03a7-8ca6-42bf-86a8-ba0ab29387a0', // FOOTHILL02 — Hipolito, Michele
  '5e8a58ca-797b-44ac-ad6d-a2c8b3bdedbf', // FOOTHILL03 — Bangston, Lydia
  '965068be-6724-41f7-853f-b0b47b26ffde', // FOOTHILL04 — Chan, Cynthia
]);

export function isFixtureUuid(uuid) {
  if (!uuid) return false;
  return FIXTURE_UUIDS.has(String(uuid));
}

// Reverse map: UUID → EHR_ID for callers that need the EHR_ID but only
// have the chart UUID. Returns null for non-fixture UUIDs.
const UUID_TO_EHR_ID = {
  'f7e19fd4-4d4f-4473-9a99-b7ede1a63212': 'SPRINT0013',
  '75367f21-56d3-4a22-a87e-60278fe890e5': 'FOOTHILL01',
  '6f9c03a7-8ca6-42bf-86a8-ba0ab29387a0': 'FOOTHILL02',
  '5e8a58ca-797b-44ac-ad6d-a2c8b3bdedbf': 'FOOTHILL03',
  '965068be-6724-41f7-853f-b0b47b26ffde': 'FOOTHILL04',
};

export function fixtureEhrIdForUuid(uuid) {
  if (!uuid) return null;
  return UUID_TO_EHR_ID[String(uuid)] || null;
}

// Compose the test-mode top-bar label.
//   getFixtureLabel('FOOTHILL04', 'Foothill Pulmonary')
//   → "Foothill Pulmonary test follow up sleep patient"
// Returns null for non-fixture EHR_IDs so callers can fall through to the
// normal clinic + date label.
export function getFixtureLabel(ehrId, clinicName) {
  const f = FIXTURES[ehrId];
  if (!f) return null;
  return `${clinicName} test ${f.encounter} ${f.subspecialty} patient`;
}

export function getFixtureMeta(ehrId) {
  return FIXTURES[ehrId] || null;
}

// Returns array of {name, specialty, fax, email}. Empty array for non-fixtures.
export function getFixturePcps(ehrId) {
  return FIXTURES[ehrId]?.pcps || [];
}

export function getFixtureConsultants(ehrId) {
  return FIXTURES[ehrId]?.consultants || [];
}
